/**
 * Desktop Search Component
 * Desktop (≥ 768px): Shows inline autocomplete dropdown with BaseSearch.
 * Mobile (< 768px): Dispatches mobile-search:open for the MobileSearchDrawer.
 */

import { loadSearchCore } from "@stardust/search-loader";
import { lockScroll, unlockScroll } from "@stardust/scroll-lock";
import { FocusTrap } from "@stardust/focus-trap";
import { SCREENS } from "@stardust/constants";
import Slider from "@stardust/slider";

// Breakpoint matching header's mobile/desktop layout split
const DESKTOP_MQ = `(min-width: ${SCREENS.md}px)`;
const DROPDOWN_ANIMATION_MS = 300;
const DROPDOWN_OPEN_CLASSES = ['opacity-100', 'translate-y-0'];
const DROPDOWN_CLOSED_CLASSES = ['opacity-0', '-translate-y-2', 'pointer-events-none'];

export default class DesktopSearch {
  /**
   * @param {HTMLElement} header - The [data-store-header] element
   */
  constructor(header) {
    this.header = header;
    // Scope to the desktop search block — the mobile drawer's search node also
    // carries [data-search-input]/[data-search-results] inside this header.
    const root = header.querySelector('[data-desktop-search]') || header;
    this.searchInput = root.querySelector('[data-search-input]');
    this.closeButton = root.querySelector('[data-search-close]');
    this.dropdown = root.querySelector('[data-search-results]');

    // Desktop search instance (composed) — constructed lazily on first open,
    // so the heavy @stardust/search graph never loads without search intent.
    this._search = null;
    this._searchReady = null;
    this._isDropdownOpen = false;
    // Loops Tab between the search bar (input + close) and the open dropdown.
    // Built on first open — the bar and dropdown are the only two children of
    // the wrapper the trap is mounted on (see core-header.liquid).
    this._focusTrap = null;
    this._closeTimer = null;
    this._openRaf = null;

    // Media query state
    this._mql = window.matchMedia(DESKTOP_MQ);
    this._isDesktop = this._mql.matches;

    // Products slider — always active while the dropdown exists (see
    // _createProductSlider)
    this._slider = null;
    this._sliderRoot = null;
    this._sliderObserver = null;

    // Bound handlers for cleanup
    this._onFocusOrClick = this._handleFocusOrClick.bind(this);
    this._onClickOutside = this._handleClickOutside.bind(this);
    this._onEscape = this._handleEscape.bind(this);
    this._onCloseClick = this._handleCloseClick.bind(this);
    this._onMediaChange = this._handleMediaChange.bind(this);
    // Bound once here, but only attached while the dropdown is open.
    this._onResize = this._positionDropdown.bind(this);
  }

  load() {
    if (!this.searchInput) {
      console.warn('[DesktopSearch] Search input not found');
      return;
    }

    // The products row is always a horizontal slider. Wire it up now.
    if (this.dropdown) {
      this._sliderRoot = this.dropdown.querySelector('[data-search-products-slider]');
      if (this._sliderRoot) {
        this._createProductSlider();
      }
    }

    // URL query (`?q=`) is synced into the input by search-loader.js, which
    // loads ahead of this module — don't redo it here.

    // Attach listeners
    this.searchInput.addEventListener('focus', this._onFocusOrClick);
    this.searchInput.addEventListener('click', this._onFocusOrClick);
    this.closeButton?.addEventListener('click', this._onCloseClick);
    document.addEventListener('click', this._onClickOutside, true);
    document.addEventListener('keydown', this._onEscape, true);
    this._mql.addEventListener('change', this._onMediaChange);
  }

  /**
   * Lazily import the search core and wire a BaseSearch instance to the
   * dropdown, memoized. First call kicks off the import; the dropdown is
   * already open on server-rendered content (skeletons under SearchSpring)
   * while it loads.
   * @returns {Promise<BaseSearch | null>} null if the core failed to load
   *   (memo reset so the next open retries)
   * @private
   */
  _ensureSearch() {
    if (!this._searchReady) {
      this._searchReady = loadSearchCore().then((BaseSearch) => {
        if (!BaseSearch) {
          this._searchReady = null;
          return null;
        }
        const search = new BaseSearch();
        search.searchInput = this.searchInput;
        search.searchesTitle = this.dropdown.querySelector('[data-searches-title]');
        search.searchesList = this.dropdown.querySelector('[data-searches-list]');
        search.productsTitle = this.dropdown.querySelector('[data-products-title]');
        search.productsGrid = this.dropdown.querySelector('[data-products-grid]');
        search.searchResultsStatus = this.dropdown.querySelector('[data-search-results-status]');
        search.searchesSection = this.dropdown.querySelector('[data-autocomplete-searches]');
        search.productsSection = this.dropdown.querySelector('[data-autocomplete-products-section]');
        search.emptyState = this.dropdown.querySelector('[data-empty-state]');
        search.emptyText = this.dropdown.querySelector('[data-empty-text]');
        search.featuredProductsContainer = this.dropdown.querySelector('[data-featured-products-container]');
        search.featuredProductsTitle = this.dropdown.querySelector('[data-featured-products-title]');
        search.helpfulPagesSection = this.dropdown.querySelector('[data-helpful-pages-section]');
        search.viewAllContainer = this.dropdown.querySelector('[data-autocomplete-view-all]');
        search.viewAllLink = this.dropdown.querySelector('.autocomplete__search-view-all-link');

        // Override getMaxProducts to use the data attribute
        const trendingCount = parseInt(this.dropdown.dataset.trendingCount, 10);
        if (trendingCount) {
          search.getMaxProducts = () => trendingCount;
        }

        search.initSearch();
        this._search = search;
        return search;
      });
    }
    return this._searchReady;
  }

  // --- Event handlers ---

  _handleFocusOrClick(e) {
    // Prevent reopen race after a modal was just closed
    if (window.__modalJustClosedAt && (Date.now() - window.__modalJustClosedAt) < 150) return;

    if (this._isDesktop) {
      this._openDropdown();
    } else {
      // Blur the input so focus doesn't stay behind the modal
      this.searchInput.blur();
      document.dispatchEvent(new CustomEvent('mobile-search:open'));
    }
  }

  _handleClickOutside(e) {
    if (!this._isDropdownOpen) return;
    // Close if the click is outside the search bar + dropdown. The dropdown element
    // is the visible panel in both modes — full-bleed in Full width, the centered
    // page-width box in Flyout — so testing it directly handles side clicks correctly.
    const searchBar = this.searchInput.closest('[data-search-bar]');
    if (searchBar && !searchBar.contains(e.target) && !this.dropdown.contains(e.target)) {
      this._closeDropdown();
    }
  }

  _handleEscape(e) {
    if (e.key === 'Escape' && this._isDropdownOpen) {
      e.stopPropagation();
      this._closeDropdown();
      this.searchInput.blur();
    }
  }

  _handleCloseClick() {
    if (!this._isDesktop) return;
    this._closeDropdown();
    this.searchInput.blur();
  }

  _handleMediaChange(e) {
    this._isDesktop = e.matches;
    // If we were desktop and switched to mobile, close the dropdown
    if (!this._isDesktop && this._isDropdownOpen) {
      this._closeDropdown();
    }
  }

  // --- Dropdown visibility ---

  /**
   * Anchor the fixed dropdown below the nav row and cap its height to the space
   * left underneath. [data-search-dropdown-anchor] is a zero-height marker just
   * above the navigation, so its bottom sits at the nav's top edge and the
   * dropdown opens from there, covering the nav. Without the cap the panel is
   * as tall as its content and its `overflow-y-auto` never engages, so on short
   * screens the helpful pages and part of the product cards fall off-screen.
   * @private
   */
  _positionDropdown() {
    if (!this.dropdown) return;
    const dropdownAnchor = this.header.querySelector('[data-search-dropdown-anchor]');
    const anchorRect = dropdownAnchor?.getBoundingClientRect();
    const headerRect = this.header.getBoundingClientRect();
    const top = anchorRect?.bottom ?? headerRect.bottom;
    this.dropdown.style.top = `${top}px`;
    this.dropdown.style.maxHeight = `calc(100dvh - ${top}px)`;
  }

  _applyDropdownOpenState() {
    this.dropdown.classList.remove(...DROPDOWN_CLOSED_CLASSES);
    this.dropdown.classList.add(...DROPDOWN_OPEN_CLASSES);
  }

  _applyDropdownClosedState() {
    this.dropdown.classList.remove(...DROPDOWN_OPEN_CLASSES);
    this.dropdown.classList.add(...DROPDOWN_CLOSED_CLASSES);
  }

  _openDropdown() {
    if (this._isDropdownOpen || !this.dropdown) return;
    this._isDropdownOpen = true;
    clearTimeout(this._closeTimer);

    this._positionDropdown();
    // Short viewports (laptops still wide enough for the desktop layout) can be
    // shorter than the dropdown's content, so re-cap on resize and rotation.
    window.addEventListener('resize', this._onResize);

    this.dropdown.classList.remove('hidden');
    this._openRaf = requestAnimationFrame(() => {
      this._openRaf = null;
      if (!this._isDropdownOpen || !this.dropdown) return;
      this._applyDropdownOpenState();
      // Recompute arrow enabled/disabled now that the dropdown has real dimensions.
      this._slider?.refresh();
    });
    this.closeButton?.classList.remove('hidden');
    lockScroll();

    // Trap Tab inside search bar + dropdown while open. ESC and outside
    // clicks stay this class's job (escapeDeactivates off, no overlay) —
    // both paths land in _closeDropdown, which deactivates the trap.
    if (!this._focusTrap) {
      this._focusTrap = new FocusTrap(this.dropdown.parentElement, {
        initialFocus: '[data-search-input]',
        escapeDeactivates: false,
        returnFocusOnDeactivate: false,
      });
    }
    this._focusTrap.activate();

    // The dropdown is open on server-rendered content; load the search core
    // behind it and sync with the input's current value (?q= prefill or
    // anything typed while it loaded). Skip the sync if the user already
    // closed the dropdown by the time the core lands.
    this._ensureSearch().then((search) => {
      if (search && this._isDropdownOpen) search.syncSearch();
    });
  }

  _closeDropdown() {
    if (!this._isDropdownOpen || !this.dropdown) return;
    this._isDropdownOpen = false;
    if (this._openRaf !== null) {
      cancelAnimationFrame(this._openRaf);
      this._openRaf = null;
    }

    this._applyDropdownClosedState();
    clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => {
      this.dropdown.classList.add('hidden');
    }, DROPDOWN_ANIMATION_MS);
    this.closeButton?.classList.add('hidden');
    window.removeEventListener('resize', this._onResize);
    unlockScroll();
    this._focusTrap?.deactivate();

    // Keep the typed/selected query in the input and its results state intact —
    // closing without searching shouldn't reset it (matches mobile-search-drawer.js;
    // there's no separate "clear" affordance on desktop, closing is the only action).
  }

  // --- Products slider ---

  /**
   * Build the Slider on the products row and keep it in sync as search.js swaps
   * the grid contents (trending ⇄ results ⇄ loading skeletons). Cards injected
   * by the shared search module are bare, so we wrap them in [data-slider-item]
   * slides — mirroring the collection-slider markup — for the Slider to drive.
   * No-ops if a slider already exists.
   * @private
   */
  _createProductSlider() {
    if (this._slider || !this._sliderRoot) return;

    const list = this._sliderRoot.querySelector('[data-slider-list]');
    if (!list) return;

    this._wrapProductSlides(list);
    // rootTabStop: false — every card has its own tabbable links, so a
    // focusable root would only add an empty tab stop (arrow keys still
    // work: the handler hears keydown bubbling from the focused card).
    this._slider = new Slider(this._sliderRoot, { snap: false, snapOnRelease: true, rootTabStop: false });

    // search.js replaces the grid content via innerHTML/appendChild with no
    // callback, so observe it: wrap any newly-injected bare cards and refresh.
    // resetToStart() so a fresh set of results (new query, restored defaults)
    // always opens on the first slide instead of staying at whatever position
    // the previous content was scrolled to.
    this._sliderObserver = new MutationObserver(() => {
      if (!this._sliderObserver || !this._slider) return;
      // Pause so our own wrapping mutations don't re-trigger the callback.
      this._sliderObserver.disconnect();
      this._wrapProductSlides(list);
      this._slider.resetToStart();
      this._slider.refresh();
      this._sliderObserver.observe(list, { childList: true });
    });
    this._sliderObserver.observe(list, { childList: true });
  }

  /**
   * Wrap bare product cards (direct children of the list) in slide elements so
   * the Slider treats them as items. SSR cards are already wrapped; cards added
   * by search.js are not. Idempotent — already-wrapped cards are skipped. Uses
   * the same .collection-slider__slide sizing as the Collection Slider section
   * (frontend/styles/components/product-carousel-slide.css) so cards match.
   * @param {HTMLElement} list - The [data-slider-list] / [data-products-grid] element
   * @private
   */
  _wrapProductSlides(list) {
    list.querySelectorAll(':scope > .product-card').forEach((card) => {
      const slide = document.createElement('div');
      slide.className = 'collection-slider__slide shrink-0';
      slide.dataset.sliderItem = '';
      card.before(slide);
      slide.appendChild(card);
    });
  }
}

// Self-initialize
if (typeof document !== 'undefined') {
  const init = () => {
    const element = document.querySelector('[data-store-header]');
    if (element) {
      const search = new DesktopSearch(element);
      search.load();
    }
  };

  init();
}
